<template>
  <div>
    <TitleBar>
      <template v-slot:title>
        <translate>Financial Statements</translate>
      </template>
      <template v-slot:buttons>
        <div class="report-progress-wrapper">
          <b-progress v-if="progressBar" :value="progressBarValue" :max="progressBarMax" :class="'report-progress'" />
          <div class="report-title-buttons">
            <OnboardingIntroLink :onboarding-intro="'onboarding_web_financial_report'" />
            <GekkoButton 
              v-if="!$route.path.includes('control-panel')"
              :text="$gettext('Investments')" 
              :to="{ name: 'BalanceSheet' }"
              icon="fa-solid fa-up-right-from-square"
              :icon-right="true"
              variant="link"
              class="mr-2"
            />
            <GekkoButton 
              v-if="pageState == 'report_table'" 
              @click="calculateAllYears()" 
              :text="$gettext('Calculate All')" 
              icon="fa-solid fa-arrows-rotate"
              class="ml-2 my-2"
            />
            <GekkoButton 
              v-if="pageState === 'report_table'"
              @click="download()" 
              :text="$gettext('Download')" 
              icon="fa-solid fa-download"
              variant="outline-primary"
              class="ml-2 my-2"
            />
          </div>
        </div>
      </template>
    </TitleBar>
    <div class="content">
      <LoadingSpinner v-if="loading" />
      <ReportTable 
        v-show="reportDetails && pageState === 'report_table'"
        ref="reporttable"
        :details="reportDetails"
        @showProgressBar="showProgressBar"
        @endCalculating="endCalculating"
        @increaseProgressBarValue="increaseProgressBarValue"
        @setProgressBarMax="setProgressBarMax"
      />
      <div v-show="pageState !== 'report_table'">
        <EmptyState>
          <template v-slot:image>
            <div class="image-text-container">
              <div class="image-container"><img src="/static/img/empty-states/empty-state-cost.png"></div>
              <div v-if="pageState === 'set_starting_year'">
                <div class="text-container empty-state--content" v-translate>If you do not input any numbers, Gekko will assume 0 assets and 0 equity at the start of your Balance Sheet history.</div>
              </div>
            </div>
          </template>
          <template v-slot:title>
            <div v-if="pageState === 'no_report_badge'">{{ $gettext('No financial statements') }}</div>
            <div v-if="pageState === 'set_starting_year'">{{ $gettext('Set your starting Balance Sheet') }}</div>
          </template>
          <template v-slot:content>
            <b-form v-if="pageState === 'set_starting_year'">
              <div class="table-cell">
                <b-form-group label-class="gk-label-margin gk-modal-label">
                    <template slot="label">
                        <span v-translate>Balance Bank Account</span>
                    </template>
                    <b-form-input 
                      v-model="reportStartDetails.balance_bank_account"
                      size="sm"
                      type="number"/>
                </b-form-group>
              </div>
              <div class="table-cell">
                <b-form-group label-class="gk-label-margin gk-modal-label">
                    <template slot="label">
                        <span v-translate>Cash & Money equivalents</span>
                    </template>
                    <b-form-input 
                      v-model="reportStartDetails.cash"
                      size="sm"
                      type="number"/>
                </b-form-group>
              </div>
              <div class="table-cell">
                <b-form-group label-class="gk-label-margin gk-modal-label">
                    <template slot="label">
                        <span v-translate>Accounts Receivable</span>
                    </template>
                    <b-form-input 
                      v-model="reportStartDetails.accounts_receivable"
                      size="sm"
                      type="number"/>
                </b-form-group>
              </div>
              <div class="table-cell">
                <b-form-group label-class="gk-label-margin gk-modal-label">
                    <template slot="label">
                        <span v-translate>Investments</span>
                    </template>
                    <b-form-input 
                      v-model="reportStartDetails.investments"
                      size="sm"
                      type="number"/>
                </b-form-group>
              </div>
              <div class="table-cell">
                <b-form-group label-class="gk-label-margin gk-modal-label">
                    <template slot="label">
                        <span v-translate>Unpaid VAT</span>
                    </template>
                    <b-form-input 
                      v-model="reportStartDetails.unpaid_vat"
                      size="sm"
                      type="number"/>
                </b-form-group>
              </div>
              <div class="table-cell">
                <b-form-group label-class="gk-label-margin gk-modal-label">
                    <template slot="label">
                        <span v-translate>Accounts Payable</span>
                    </template>
                    <b-form-input 
                      v-model="reportStartDetails.unpaid_expenses"
                      size="sm"
                      type="number"/>
                </b-form-group>
              </div>
              <div class="table-cell">
                <b-form-group label-class="gk-label-margin gk-modal-label">
                    <template slot="label">
                        <span v-translate>Other Debts</span>
                    </template>
                    <b-form-input 
                      v-model="reportStartDetails.other_debts"
                      size="sm"
                      type="number"/>
                </b-form-group>
              </div>
            </b-form>
            <div v-if="pageState === 'no_report_badge'">
              {{ $gettext('Create a new report by clicking on the button below.') }}
            </div>
          </template>
          <template v-slot:cta>
            <GekkoButton
              v-if="pageState === 'no_report_badge'"
              @click="setStartingYear()"
              :text="$gettext('Calculate')"
              icon="fa-solid fa-play"
            />
            <GekkoButton
              v-if="pageState === 'set_starting_year'"
              @click="createStartYear()"
              :text="$gettext('Create report')"
              icon="fa-solid fa-plus"
            />
          </template>
        </EmptyState>
      </div>
    </div>
  </div>
</template>

<script>
import { promptDownload } from "@/utils/"

import TitleBar from "@/components/TitleBar";
import GekkoButton from "@/components/GekkoButton";
import FinancialReportingService from "@/store/services/financialreporting.service";
import CompanyService from "@/store/services/company.service";
import ReportTable from "@/components/balance_sheet/ReportTable";
import LoadingSpinner from "@/components/dashboard/ui/LoadingSpinner"
import OnboardingIntroLink from "@/components/ui/OnboardingIntroLink"
import EmptyState from "@/components/ui/EmptyState"
import { mapGetters } from "vuex";

export default {
  name: "FinancialReporting",
  components: { 
    TitleBar,
    GekkoButton,
    ReportTable,
    LoadingSpinner,
    OnboardingIntroLink,
    EmptyState
  },
  data () {
    return {
      reportDetails: null,
      reportStartDetails: null,
      pageState: "report_table",
      progressBar: false,
      progressBarValue: 0,
      progressBarMax: null,
      loading: true,
      calculating: false
    }
  },
  created () {
    FinancialReportingService.list({ company_profile: this.getCompanyId }).then((response) => {
      if (!response.data.length) {
        this.pageState = "no_report_badge"
      }
      this.loading = false
      this.reportDetails = response.data
    });
  },
  computed: {
    ...mapGetters ({
        getCompanyId: 'main/getCompanyId',
        currentUser: "main/getCurrentUser"
    })
  },
  methods: {
    download() {
      FinancialReportingService.getFinancialStatementsPDFBlob({ company_profile: this.getCompanyId }).then(response => {
        promptDownload(response)
      }).catch(error => console.error('Error downloading PDF:', error));
    },
    setStartingYear() {
      this.reportStartDetails = {
        created_by: this.currentUser.id,
        company_profile: this.getCompanyId,
        year: null,
        is_start_year: true,
        revenue: 0,
        unpaid_invoices: 0,
        paid_invoices: 0,
        corrections: 0,
        total_costs: 0,
        expenses: 0,
        write_offs: 0,
        profit: 0,
        assets: 0,
        balance_bank_account: 0,
        cash: 0,
        accounts_receivable: 0,
        investments: 0,
        liabilities: 0,
        unpaid_vat: 0,
        unpaid_expenses: 0,
        other_debts: 0,
        equity: 0,
        withdrawal: 0
      }

      this.pageState = "set_starting_year";
    },
    createStartYear() {
      var isPremium = this.$store.getters['getIsPremium'];
      if (isPremium || this.currentUser.is_accountant) {
        FinancialReportingService.create(this.reportStartDetails).then((response) => {
          this.$refs.reporttable.addReportToTable(response.data);
          this.pageState = "report_table"

          this.calculate();
        }).catch(() => {
          this.$store.commit("setAlert", {
          title: this.$gettext("Error"),
          text: this.$gettext("Something went wrong."),
          variant: "danger",
          duration: 4
          });
        });
      } else {
        this.$store.commit('togglePremiumModal', 'web_reports_calculate');
      }
    },
    calculateAllYears() {
      var isPremium = this.$store.getters['getIsPremium'];
      if (isPremium || this.currentUser.is_accountant) {
        this.calculate();
      }
    },
    calculate () {
      if (!this.calculating) {
        this.calculating = true
        CompanyService.financiallyActiveYears().then((response) => {
          if (response.data.length > 0) {
            let years = [];
            if (response.data.length === 1) {
              years = [response.data[0]];
            } else {
              let minYear = response.data.pop(); // the response is sorted
              let maxYear = response.data.shift();
              years = [...Array(maxYear - minYear + 1).keys()].map(x => x + parseInt(minYear));
            }

            this.setProgressBarMax(years.length);
            this.showProgressBar();
            
            this.$refs.reporttable.removeReportsFromList(years);
            this.$refs.reporttable.recalculateNextYear(years);
          } else {
            this.$store.commit("setAlert", {
              title: this.$gettext("Data not available"),
              text: this.$gettext("No financial activity detected."),
              variant: "danger",
              duration: 4
            });
          }
        });
      }
    },
    showProgressBar() {
      this.progressBar = true;
    },
    hideProgressBar() {
      this.progressBar = false;
      this.progressBarValue = 0;
    },
    endCalculating() {
      this.calculating = false
      this.hideProgressBar()
    },
    increaseProgressBarValue() {
      this.progressBarValue++;
    },
    setProgressBarMax(maxValue) {
      this.progressBarMax = maxValue;
    }
  }
}
</script>

<style lang="scss">
.financialstatements {
  display: flex;

  @media(max-width: '767px') {
    .titlebar {
      .pagetitle {
        h2 { font-size: 26px; line-height: 1;}
      }
      .action-bar {
        width: 100%;
      }
    }
  }
  > .content {
    padding: 0;
    position: relative;
    flex: 1;
  }
  .report-progress-wrapper {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    flex: 1;
    .progress.report-progress {
      width: 100%;
      box-shadow: none;
      border-radius: $radiusXl;
      background: #f6f6f6;
      height: 7px;
      margin-top: 0.5em;
      margin-bottom: 0.5em;
    }
    .report-title-buttons {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
    }
    @media(min-width: '576px') {
      flex-direction: row-reverse;
      align-items: center;
      // justify-content: flex-end;
      .progress.report-progress { flex: 1; }
    }
    @media(min-width: '768px') {
      flex-direction: row;
      align-items: center;
      justify-content: flex-end;
      .progress.report-progress {
        margin-right: 1rem;
        margin-left: 1rem;
      }
      .report-title-buttons {
        flex-direction: row;
        align-items: center;
      }
    }
  }
}


</style>

<style scoped>
.image-container {
  width: fit-content;
}

.image-text-container {
  display: flex;
  flex-direction: column;
}

.text-container {
  max-width: 328px;
  margin-top: 20px;
  padding: 10px;
}
</style>